import { Promise } from 'bluebird';
import _ from 'underscore';
import classNames from 'classnames';
import React, { Fragment, Suspense } from 'react';
import PropTypes from 'prop-types';
import { PredicateUIType, PredicateType } from '@bingads-webui/filter-commons';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getHumanizedPredicateStyles } from './humanized-predicate-style';

function getValuesAndMoreText(
  i18n,
  items,
  getPredicateIconClassName = _.noop,
  getName = _.identity
) {
  const MAX_ITEMS_TO_SHOW = 2;
  const ITEMS_MAX_LENGTH = 50;
  const { namesAndIcon } = _.reduce(items, (memo, id) => {
    const memoCopy = memo;
    if (_.size(memoCopy.namesAndIcon) < MAX_ITEMS_TO_SHOW
      && memoCopy.totalLength < ITEMS_MAX_LENGTH) {
      memoCopy.namesAndIcon.push({
        name: getName(id),
        iconClassName: getPredicateIconClassName(id),
      });
      memoCopy.totalLength += _.size(_.last(memoCopy.namesAndIcon));
    }

    return memoCopy;
  }, { totalLength: 0, namesAndIcon: [] });

  const notShowingCount = _.size(items) - _.size(namesAndIcon);

  return {
    values: namesAndIcon,
    moreText: notShowingCount ? i18n.getString(_TL_('And {{remain}} more'), {
      remain: notShowingCount,
    }) : null,
  };
}


export function getValuesAndMoreTextForSelector(
  i18n,
  valuesInQuery,
  property,
  getPredicateIconClassName = _.noop
) {
  const MAX_ITEMS_TO_SHOW = 5;
  let itemsPromise;
  if (property.dataSource && !_.isEmpty(valuesInQuery)) {
    // Asking for one over the limit so that "and more" logic can be triggered
    itemsPromise = property.dataSource.getByIds(valuesInQuery.slice(0, MAX_ITEMS_TO_SHOW + 1));
  } else if (property.items) {
    itemsPromise = Promise.resolve(property.items);
  } else {
    itemsPromise = Promise.resolve([]);
  }

  return itemsPromise
    .then((items) => {
      const idInfoMap = _.indexBy(items, 'Id');
      return getValuesAndMoreText(
        i18n,
        valuesInQuery,
        id => (_.has(idInfoMap, id) ? getPredicateIconClassName(idInfoMap[id]) : undefined),
        id => (_.has(idInfoMap, id) ? idInfoMap[id].Name : i18n.getString(_TL_('(deleted)')))
      );
    });
}

function getIconElem(value, classes) {
  if (!value || !value.iconClassName) {
    return null;
  }
  return (
    <div className={classNames(classes.humanizedPredicateIcon)}>
      <div className={classNames(value.iconClassName)} />
    </div>
  );
}

function getHumanizedPredicateEditor(options, valuesAndMore, classes, textOnly) {
  const length = valuesAndMore && valuesAndMore.values ? valuesAndMore.values.length : 0;
  let isValueContainIcon = false;
  _.each(valuesAndMore.values, (value) => {
    if (value && value.iconClassName) {
      isValueContainIcon = true;
    }
  });
  const text = isValueContainIcon ? (
    <Fragment>
      {`${options.property} ${options.operator}`}
      {valuesAndMore.values.map((value, i) => (
        <Fragment key={value.name}>
          { getIconElem(value, classes)}
          {` ${value.name}`}{i !== length - 1 && length !== 1 && ','}
        </Fragment>
          ))
          }
      {valuesAndMore.moreText ? <span className="type-bold">{` ${valuesAndMore.moreText}`}</span> : null}
    </Fragment>) : (
      <Fragment>
        {`${options.property} ${options.operator} ${_.pluck(valuesAndMore.values, 'name').join(', ')}`}
        {valuesAndMore.moreText ? <span className="type-bold">{` ${valuesAndMore.moreText}`}</span> : null}
      </Fragment>);
  return (
    textOnly ? text :
    <div className="humanized-predicate">
      {text}
    </div>
  );
}

function HumanizedPredicateUnthemed(props) {
  const {
    property, operator, value, uiType,
  } = props.predicate;
  const { getPredicateIconClassName, classes, textOnly } = props;
  const metadata = props.i18nConverter.schema.getFilterProperty(property);
  if (!metadata) {
    throw new Error(`Property ${property} is not filterable`);
  }
  const propertyLocalized = metadata.title
    ? metadata.title : props.i18nConverter.convertName(property);

  if (uiType === PredicateUIType.Selector) {
    const text = `${propertyLocalized}: ${props.predicate.getHumanizedValue()}`;
    return (
      textOnly ? text : <div className="humanized-predicate">{text}</div>
    );
  }

  const humanizedPredicateEditorOptions = {
    uiType,
    property: propertyLocalized,
    operator: props.i18nConverter.convertOperator(operator, metadata.operatorDisplayNameMap),
    values: _.map(
      _.isArray(value) ? value : [value],
      val => props.i18nConverter.convertValue(val, property)
    ),
  };

  if (metadata.type === PredicateType.Selector) {
    const HumanizedPredicateEditor = React.lazy(() =>
      getValuesAndMoreTextForSelector(
        props.i18n,
        humanizedPredicateEditorOptions.values, metadata, getPredicateIconClassName
      )
        .then(valuesAndMore => ({
          default: () =>
            getHumanizedPredicateEditor(
              humanizedPredicateEditorOptions,
              valuesAndMore,
              classes,
              textOnly
            ),
        })));
    return (
      <Suspense fallback={<div />}>
        <HumanizedPredicateEditor />
      </Suspense>
    );
  }
  const valuesAndMore = getValuesAndMoreText(
    props.i18n,
    humanizedPredicateEditorOptions.values,
    getPredicateIconClassName,
    props.predicateValueNameGetter ? props.predicateValueNameGetter(metadata.name) : undefined
  );
  return getHumanizedPredicateEditor(
    humanizedPredicateEditorOptions,
    valuesAndMore, classes, textOnly
  );
}

export const HumanizedPredicate =
  withDefaultStyles(HumanizedPredicateUnthemed, getHumanizedPredicateStyles);

HumanizedPredicateUnthemed.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  i18nConverter: PropTypes.shape({
    schema: PropTypes.shape({
      getFilterProperty: PropTypes.func,
    }),
    convertName: PropTypes.func,
    convertOperator: PropTypes.func,
    convertValue: PropTypes.func,
  }).isRequired,
  predicate: PropTypes.shape({
    getHumanizedValue: PropTypes.func,
    uiType: PropTypes.number,
    property: PropTypes.string,
    operator: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }).isRequired,
  getPredicateIconClassName: PropTypes.func,
  predicateValueNameGetter: PropTypes.func,
  classes: PropTypes.shape({
    humanizedPredicateIcon: PropTypes.string.isRequired,
  }).isRequired,
};

